import { FIGHT_DATA_ACTIONS } from "../Actions/fightDataActions";
import { log } from "../../Helpers/logger";

// Fonction pour créer un monstre par défaut
const createDefaultMonster = () => ({
    id: 'monster_default',
    name: 'Monstre',
    stats: {
        health: 25,
        maxHealth: 25,
        attack: 5,
        defense: 2,
        speed: 3
    },
    level: 1,
    xp: 10,
    gold: 5
});

const FIGHT_DATA_INITIAL_STATE = {
    monsters: [createDefaultMonster()], // Au moins un monstre par défaut
    currentMonstersIndex: 0,
    endFightData: {
        expDrop: 10,
        goldDrop: 5
    },
    // Effets actifs sur les monstres
    monsterEffects: {}
};

const fightDataReducer = (state = FIGHT_DATA_INITIAL_STATE, action = null) => {
    // log.reducer.log('fightDataReducer - Action:', action);
    if (!action) {
        return state;
    }   

    switch (action.type) {
        case FIGHT_DATA_ACTIONS.SET_END_FIGHT_DATA: {
            const { expDrop, goldDrop } = action.payload;
            return {
                ...state,
                endFightData: {
                    expDrop,
                    goldDrop
                }
            }
        }
        case FIGHT_DATA_ACTIONS.MONSTER_TAKE_DAMAGE_BY_ID: {
            const { monstersIndex, amount } = action.payload;
            
            // Vérifier que l'index est valide
            if (monstersIndex === undefined || !state.monsters || !state.monsters[monstersIndex]) {
                console.error('Index de monstre invalide:', monstersIndex);
                return state;
            }
            
            const monster = state.monsters[monstersIndex];
            
            // S'assurer que le monstre a bien des statistiques
            if (!monster.stats) {
                monster.stats = { health: 0, maxHealth: 0 };
            }
            
            log.reducer.log("fightDataReducer - Monstre avant dégâts:", monster);
            
            // Créer une copie profonde du monstre pour forcer le rendu
            const nextMonster = {
                ...monster,
                stats: {
                    ...monster.stats,
                    health: Math.max(0, monster.stats.health - amount) // S'assurer que les PV ne descendent pas en dessous de 0
                }
            };
            
            // Créer un nouveau tableau de monstres avec le monstre mis à jour
            const nextMonsters = state.monsters.map((m, index) => 
                index === monstersIndex ? nextMonster : m
            );
            
            log.reducer.log('fightDataReducer - Monstre mis à jour:', { 
                index: monstersIndex, 
                oldHealth: monster.stats.health, 
                newHealth: nextMonster.stats.health,
                damage: amount 
            });

            return {
                ...state,
                monsters: nextMonsters
            }
        }
        case FIGHT_DATA_ACTIONS.REMOVE_MONSTER_BY_INDEX: {
            const { monstersIndex } = action.payload;
            // Créer un nouveau tableau sans le monstre à l'index spécifié
            const nextMonsters = state.monsters.filter((_, index) => index !== monstersIndex);
            log.reducer.log('fightDataReducer - Monstre retiré:', { index: monstersIndex, remaining: nextMonsters.length });

            // Supprimer aussi les effets liés à ce monstre
            const nextMonsterEffects = { ...state.monsterEffects };
            delete nextMonsterEffects[monstersIndex];

            return {
                ...state,
                monsters: nextMonsters,
                currentMonstersIndex: 0,
                monsterEffects: nextMonsterEffects
            };
        }
        
        case 'FIGHT_DATA/MONSTER/APPLY_EFFECT': {
            const { monsterIndex, effect } = action.payload;
            if (monsterIndex === undefined || !effect) return state;
            
            // Créer une copie des effets actuels
            const currentEffects = state.monsterEffects[monsterIndex] || [];
            
            // Vérifier si un effet similaire existe déjà
            const effectIndex = currentEffects.findIndex(e => e && e.type === effect.type);
            
            let nextEffects;
            if (effectIndex >= 0) {
                // Remplacer l'effet existant
                nextEffects = [...currentEffects];
                nextEffects[effectIndex] = { ...effect };
            } else {
                // Ajouter le nouvel effet
                nextEffects = [...currentEffects, { ...effect }];
            }
            
            // Mettre à jour les effets du monstre
            return {
                ...state,
                monsterEffects: {
                    ...state.monsterEffects,
                    [monsterIndex]: nextEffects
                }
            };
        }
        
        case 'FIGHT_DATA/MONSTER/PROCESS_EFFECTS': {
            // Traiter les effets de tous les monstres
            const { monsterEffects, monsters } = state;
            if (!monsters || !monsterEffects) return state;
            
            const nextMonsters = [...monsters];
            const updatedEffects = { ...monsterEffects };
            
            // Parcourir tous les monstres avec des effets
            Object.keys(monsterEffects).forEach(monsterIndex => {
                const index = parseInt(monsterIndex, 10);
                if (index >= nextMonsters.length) {
                    // Le monstre n'existe plus, supprimer ses effets
                    delete updatedEffects[monsterIndex];
                    return;
                }
                
                const monster = nextMonsters[index];
                if (!monster) {
                    delete updatedEffects[monsterIndex];
                    return;
                }
                
                const effects = monsterEffects[monsterIndex];
                if (!Array.isArray(effects)) return;
                
                // Traiter chaque effet
                const remainingEffects = [];
                
                effects.forEach(effect => {
                    if (!effect || typeof effect !== 'object') return;
                    
                    // Créer une copie de l'effet pour la mise à jour
                    const updatedEffect = { ...effect };
                    
                    // Appliquer l'effet
                    switch(effect.type) {
                        case 'POISON': {
                            // Infliger des dégâts de poison
                            const poisonDamage = effect.damage || 5;
                            const newHealth = Math.max(0, (monster.stats.health || 0) - poisonDamage);
                            
                            // Mettre à jour les PV du monstre
                            nextMonsters[index] = {
                                ...monster,
                                stats: {
                                    ...monster.stats,
                                    health: newHealth
                                }
                            };
                            
                            // Ajouter un log
                            log.reducer.log(`${monster.name} subit ${poisonDamage} points de dégâts de poison !`);
                            break;
                        }
                            
                        case 'STUN':
                            // L'effet d'étourdissement est géré dans la logique de combat
                            log.reducer.log(`${monster.name} est toujours étourdi !`);
                            break;
                            
                        // Ajouter d'autres types d'effets ici
                        default:
                            log.reducer.warn(`Type d'effet non géré: ${effect.type}`);
                            break;
                    }
                    
                    // Décrémenter le nombre de tours restants
                    updatedEffect.remainingTurns = (updatedEffect.remainingTurns || 1) - 1;
                    
                    // Conserver l'effet s'il reste des tours
                    if (updatedEffect.remainingTurns > 0) {
                        remainingEffects.push(updatedEffect);
                    } else {
                        log.reducer.log(`L'effet ${effect.type} sur ${monster.name} a pris fin.`);
                    }
                });
                
                // Mettre à jour la liste des effets pour ce monstre
                if (remainingEffects.length > 0) {
                    updatedEffects[monsterIndex] = remainingEffects;
                } else {
                    delete updatedEffects[monsterIndex];
                }
            });
            
            return {
                ...state,
                monsters: nextMonsters,
                monsterEffects: updatedEffects
            };
        }
        default:
            return state;
    }
};

export default fightDataReducer;